import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { cardImageStyle } from "@/components/Card3D";
import { HpBar, ArenaStatChips, AttributeChips } from "@/components/ArenaBits";
import { ArrowLeft, Coins, Shield, Trophy, Tv, Users, Flame, MessageSquare, ShieldAlert } from "lucide-react";
import { ChatPanel } from "@/components/ChatPanel";

function useCountdown(targetIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);
  if (!targetIso) return 0;
  return Math.max(0, Math.ceil((new Date(targetIso).getTime() - now) / 1000));
}

function Fighter({ f, color, active }) {
  return (
    <div className={`rounded-lg border p-2 transition-all ${active ? "border-white/40 bg-white/[0.04]" : "border-white/10 bg-black/30"} ${f.hp <= 0 ? "opacity-35 grayscale" : ""}`}
      data-testid={`spectator-fighter-${f.card_id}`}>
      <div className="flex gap-2">
        <div className="relative w-12 h-16 rounded overflow-hidden bg-black shrink-0" style={{ boxShadow: active ? `0 0 12px ${color}55` : "none" }}>
          {f.front_image && <img src={f.front_image} alt={f.title} className="absolute inset-0 w-full h-full" style={cardImageStyle(f)} draggable={false} />}
          {f.hp <= 0 && <div className="absolute inset-0 flex items-center justify-center bg-black/60 font-mono text-[8px] text-red-300">KO</div>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-xs text-white truncate">{f.title}</div>
          <HpBar hp={f.hp} maxHp={f.max_hp} className="mt-1" />
          {f.shield > 0 && (
            <div className="font-mono text-[9px] text-cyan-300 inline-flex items-center gap-0.5 mt-0.5"><Shield className="h-2.5 w-2.5" /> {f.shield} shield</div>
          )}
        </div>
      </div>
      <div className="mt-1.5">
        <ArenaStatChips arena={f} compact />
        <AttributeChips attributes={f.attributes} className="mt-1" />
      </div>
    </div>
  );
}

function Corner({ side, color, active, pool, mult, totalPool }) {
  const share = totalPool > 0 ? Math.round((pool / totalPool) * 100) : 0;
  return (
    <div className="rounded-lg border border-white/10 p-2.5 bg-black/30">
      <div className="flex items-center justify-between">
        <span className="font-display text-base tracking-[0.06em]" style={{ color }}>{side.name}</span>
        <span className="font-mono text-[10px] text-amber-300 inline-flex items-center gap-0.5"><Coins className="h-3 w-3" /> {pool.toLocaleString()}</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-black/60 overflow-hidden">
        <div className="h-full transition-all duration-500" style={{ width: `${share}%`, background: color }} />
      </div>
      <div className="mt-1 flex items-center justify-between font-mono text-[9px] text-white/45">
        <span>{share}% of pool</span>
        <span>{mult != null ? `~${mult.toFixed(2)}x payout` : "—"}</span>
      </div>
    </div>
  );
}

export default function SpectatorMatchPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const qc = useQueryClient();
  const [side, setSide] = useState(0);
  const [amount, setAmount] = useState(50);
  const [busy, setBusy] = useState(false);

  const { data: m, error } = useQuery({
    queryKey: ["spectator-match", id],
    queryFn: async () => (await api.get(`/arena/spectator/matches/${id}`)).data,
    // SSE (below) pushes a live wake-up on every turn/payout; this is a slow
    // safety net in case a ping is missed.
    refetchInterval: (q) => (q.state.data?.status === "finished" ? false : 8000),
  });

  useEffect(() => {
    if (!id) return;
    const es = new EventSource(`${API}/arena/spectator/matches/${id}/stream`, { withCredentials: true });
    es.onerror = () => { api.get("/auth/me").catch(() => es.close()); };
    es.onmessage = () => { qc.invalidateQueries({ queryKey: ["spectator-match", id] }); };
    return () => es.close();
  }, [id, qc]);

  const closesIn = useCountdown(m?.bet_closes_at);
  const colors = ["#f43f5e", "#22d3ee"];

  const mults = useMemo(() => {
    if (!m) return [null, null];
    const p = [m.pools["0"], m.pools["1"]];
    return [0, 1].map((i) => {
      const other = p[1 - i];
      return p[i] > 0 ? 1 + (other * (1 - (m.profit_cut_pct ?? 0.35))) / p[i] : null;
    });
  }, [m]);

  const placeBet = async () => {
    setBusy(true);
    try {
      await api.post(`/arena/spectator/matches/${id}/bet`, { side, amount: Number(amount) });
      qc.invalidateQueries({ queryKey: ["spectator-match", id] });
      refresh();
      toast.success(`Bet placed on ${m.players[side].name}!`);
    } catch (e) { toast.error(apiError(e, "Couldn't place bet")); }
    finally { setBusy(false); }
  };

  if (error) return <div className="max-w-4xl mx-auto px-6 py-20 text-center text-white/50">Match not found. <button className="text-cyan-300 underline" onClick={() => navigate("/arena")}>Back to Arena</button></div>;
  if (!m) return <div className="max-w-4xl mx-auto px-6 py-20 text-center text-white/50">Loading match…</div>;

  const betOpen = m.status === "betting" && closesIn > 0;
  const myTotal = m.my_bets.reduce((s, b) => s + b.amount, 0);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="spectator-match-page">
      <button onClick={() => navigate("/arena")} className="inline-flex items-center gap-1 text-white/50 hover:text-white text-sm mb-4" data-testid="spectator-back">
        <ArrowLeft className="h-4 w-4" /> Back to Arena
      </button>

      {/* Status banner */}
      <div className="hud-pill rounded-2xl p-4 mb-5 text-center relative overflow-hidden">
        <div className="absolute inset-0 holo-scan opacity-20" />
        <div className="relative">
          {m.status === "betting" && (
            <>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300 inline-flex items-center gap-1.5"><Tv className="h-3.5 w-3.5" /> Betting Open</div>
              <div className="font-display text-4xl text-white mt-1 tabular-nums" data-testid="bet-countdown">{closesIn}s</div>
              <div className="text-xs text-white/45 mt-0.5">until the fight begins</div>
            </>
          )}
          {m.status === "fighting" && (
            <>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300 inline-flex items-center gap-1.5 animate-pulse"><Flame className="h-3.5 w-3.5" /> Live Battle</div>
              <div className="font-display text-2xl text-white mt-1">Bets are locked — watch it play out</div>
            </>
          )}
          {m.status === "finished" && (
            <>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Match Over</div>
              <div className="font-display text-3xl mt-1" style={{ color: colors[m.winner_side] }} data-testid="spectator-winner">
                <Trophy className="inline h-6 w-6 mb-1 mr-1" /> {m.players[m.winner_side].name} WINS
              </div>
              {m.my_payout != null && (
                <div className={`mt-1 font-mono text-sm ${m.my_payout > 0 ? "text-emerald-300" : "text-white/40"}`} data-testid="spectator-payout">
                  {m.my_payout > 0 ? `You won ${m.my_payout.toLocaleString()}c!` : "Your bets didn't land this time."}
                </div>
              )}
            </>
          )}
          <div className="mt-2 font-mono text-[11px] text-amber-300 inline-flex items-center gap-1">
            <Coins className="h-3.5 w-3.5" /> {m.total_pool.toLocaleString()}c total pot · <Users className="h-3 w-3" /> {m.bettors} bettor{m.bettors === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {/* Odds corners */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        {m.players.map((p, i) => (
          <Corner key={p.side} side={p} color={colors[i]} active={m.turn === i && m.status === "fighting"} pool={m.pools[String(i)]} mult={mults[i]} totalPool={m.total_pool} />
        ))}
      </div>

      {/* Betting panel */}
      {betOpen && (
        <div className="hud-pill rounded-2xl p-4 mb-5" data-testid="bet-panel">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">Place your bet</div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {m.players.map((p, i) => (
              <button key={p.side} data-testid={`bet-side-${i}`} onClick={() => setSide(i)}
                className={`rounded-lg border px-3 py-2 font-display text-sm transition-all ${side === i ? "border-white/50" : "border-white/10 hover:border-white/30"}`}
                style={side === i ? { color: colors[i], boxShadow: `0 0 14px ${colors[i]}33`, background: `${colors[i]}10` } : { color: "#ffffffaa" }}>
                {p.name}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Coins className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-300" />
              <Input type="number" min={m.min_bet} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="bet-amount" className="pl-9 bg-[hsl(220_18%_12%)] border-white/10" />
            </div>
            <Button data-testid="place-bet-button" disabled={busy || Number(amount) < m.min_bet} onClick={placeBet} className="bg-amber-400 text-black hover:bg-amber-300">
              {busy ? "Placing…" : "Place Bet"}
            </Button>
          </div>
          <div className="mt-1.5 font-mono text-[10px] text-white/40">
            {Math.round((m.fee_pct ?? 0.1) * 100)}% entry fee · {Math.round(amount * (1 - (m.fee_pct ?? 0.1)))}c enters the pool · min {m.min_bet}c
          </div>
          <div className="mt-2 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 flex items-start gap-2" data-testid="bet-disclaimer">
            <ShieldAlert className="h-3.5 w-3.5 text-amber-300 shrink-0 mt-0.5" />
            <p className="text-[10px] leading-snug text-amber-200/90">
              You bet against <span className="font-semibold text-amber-100">other players</span>, never the house. Winnings are paid from the shared pool. If no one bets the other side, there's nothing to win — you simply get your wager back minus the {Math.round((m.fee_pct ?? 0.1) * 100)}% house cut.
            </p>
          </div>
        </div>
      )}

      {/* One-sided-bet warning (during betting): if only ONE side has backers,
          winnings are impossible — the pari-mutuel has nothing to pay from.
          Bettors are refunded automatically at fight start, but we surface it now
          so anyone with a stake can attack the other side themselves. */}
      {m.status === "betting" && m.unopposed && m.total_pool > 0 && (
        <div data-testid="spectator-unopposed-warning" className="rounded-xl border border-amber-400/40 bg-amber-400/10 p-3 mb-4 flex items-start gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" />
          <div className="text-[12px] leading-snug">
            <span className="font-semibold text-amber-100">Only one side has bets.</span>
            <span className="text-amber-200/90"> If nobody backs the other fighter before the bell, every wager is <span className="font-semibold text-amber-100">refunded in full</span> the moment the match starts &mdash; there&apos;d be no losing pool to pay a winning pool from. Convince a friend, or bet against yourself with a different card.</span>
          </div>
        </div>
      )}
      {m.bets_refunded > 0 && (
        <div data-testid="spectator-bets-refunded" className="rounded-xl border border-cyan-400/40 bg-cyan-400/10 p-3 mb-4 flex items-start gap-2">
          <ShieldAlert className="h-4 w-4 text-cyan-300 shrink-0 mt-0.5" />
          <p className="text-[12px] leading-snug text-cyan-100">
            {m.bets_refunded} bet{m.bets_refunded === 1 ? " was" : "s were"} refunded — the match started with everyone on one side.
          </p>
        </div>
      )}

      {/* My bets */}
      {m.my_bets.length > 0 && (
        <div className="hud-pill rounded-xl p-3 mb-5 flex items-center justify-between" data-testid="my-bets">
          <span className="font-mono text-[11px] text-white/55">Your bets: {m.my_bets.map((b) => `${b.amount}c on ${m.players[b.side].name}`).join(" · ")}</span>
          <span className="font-mono text-[11px] text-amber-300">{myTotal.toLocaleString()}c</span>
        </div>
      )}

      {/* The squads */}
      <div className="grid md:grid-cols-2 gap-4 mb-5">
        {m.players.map((p, i) => (
          <div key={p.side}>
            <div className="font-display text-sm mb-2 tracking-[0.08em]" style={{ color: colors[i] }}>{p.name}</div>
            <div className="space-y-2">
              {p.squad.map((f, j) => (
                <Fighter key={f.card_id} f={f} color={colors[i]} active={m.status === "fighting" && p.active === j && m.turn === i} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Event feed (no per-move details for spectators) */}
      <div className="hud-pill rounded-xl p-3" data-testid="spectator-feed">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40 mb-1.5">Match Feed</div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {[...m.log].reverse().map((e, idx) => (
            <div key={e.t} className={`font-mono text-[11px] ${e.type === "finish" ? "text-amber-300" : e.type === "ko" ? "text-red-300" : "text-white/55"}`}>
              {e.text}
            </div>
          ))}
        </div>
      </div>

      {/* Battle chat — spectators & anyone watching */}
      <div className="hud-pill rounded-xl p-3 mt-4" data-testid="match-chat-section">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="h-4 w-4 text-cyan-300" />
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">Battle Chat</span>
        </div>
        <ChatPanel channel={`match:${id}`} testidPrefix="match-chat" heightClass="h-44" />
      </div>
    </div>
  );
}
